import { useQuery } from "@tanstack/react-query";
import { useDependencies } from "src/apps/hooks/useDependencies";
import { useAuth } from "./useAuth";
import { useSearchParams } from "react-router-dom";
import { LinkType } from "src/contexts/_shared/domain/LinkType";
import { HTTPError } from "src/contexts/_shared/domain/errors/HTTPError";
import { useDocumentationAreaIdAndActorId } from "./useDocumentationAreaIdAndActorId";
import { LoginTypes } from "src/contexts/auth/application/AuthController";

export const useGetCustomerCustomization = () => {
  const { documentationAreaController } = useDependencies();
  const { token } = useAuth();
  const { documentationAreaId } = useDocumentationAreaIdAndActorId();
  const [searchParams] = useSearchParams();
  const slug = searchParams.get("slug") ?? undefined;
  const linkType = searchParams.get("linkType") ?? undefined;

  const hasUnauthenticatedInputs = !token && !!slug && !!linkType;
  const shouldFetchAuthenticatedCustomization = !!token && !!documentationAreaId;

  const queryFn = async () => {
    if (hasUnauthenticatedInputs) {
      const parsedLinkType = new LinkType(linkType!);
      return await documentationAreaController.getCustomization(slug!, parsedLinkType.getValue());
    }

    if (shouldFetchAuthenticatedCustomization) {
      return await documentationAreaController.getCustomizationByDocumentationAreaId(
        documentationAreaId!,
        token,
      );
    }

    throw new Error("Customization query called without valid inputs");
  };

  const retryFn = (count: number, error: Error) => {
    if (count < 1) return false;
    return !(error instanceof HTTPError && error.getHTTPCode().isForbidden());
  };

  const customizationResult = useQuery({
    queryKey: hasUnauthenticatedInputs
      ? ["customerCustomization", "unauthenticated", slug, linkType]
      : ["customerCustomization", "authenticated", documentationAreaId],
    queryFn,
    retry: retryFn,
    // Every screen mounts an observer of this query. Refetching an errored query on each mount flips
    // it back to loading, which makes BaseScreen unmount its children and loops forever
    retryOnMount: false,
    enabled: hasUnauthenticatedInputs || shouldFetchAuthenticatedCustomization,
  });

  const isLoading = customizationResult.isLoading;
  const error = customizationResult.error;
  const data = customizationResult.data;
  const documentationAreaLoginMethod = customizationResult.data?.documentationAreaLoginMethod;
  const loginType = documentationAreaLoginMethod ?? LoginTypes.EMAIL_CODE;

  return { isLoading, error, data, loginType };
};
